import { cn } from '@rocco/utils/cn';

/**
 * The platform button.
 *
 * Mirrors the `Button` variable collection and master component in the
 * "ArchiPro — Button Inventory (BC-5155)" Figma file. The audit behind it found
 * 24 heights, 17 rendered corner radii, 15 type pairs and 14 background colours
 * doing the work of a handful of roles, so both ends now read from one ramp.
 *
 * Size is a package deal — height, corner, horizontal padding and label size
 * move together. Never take a height from one step and a corner from another:
 * the corner is scaled to the height on purpose, because a radius above half
 * the height clamps back to a pill and the step stops reading.
 *
 * Sizes are rem so they hit the Figma px at the 1920 anchor and still scale
 * with browser zoom.
 */
export type ButtonSize = 'xs' | 's' | 'm' | 'l' | 'xl';

/**
 * One step at every width, or a phone step and the one it grows to at `md`.
 *
 * A control that sits on a tile needs both: the same button that reads right
 * beside a 900px frame overruns a 176px portrait on a phone. The pair belongs
 * here rather than in `extra`, which must never carry a height, a corner or a
 * padding — taking one of those from a second step is how a button ends up
 * between the ramp's rungs.
 */
export type ButtonStep =
    | ButtonSize
    | readonly [phone: ButtonSize, desktop: ButtonSize];

export type ButtonKind =
    | 'primary' // dark fill on a light ground — the default action
    | 'secondary' // hairline on a light ground
    | 'muted' // quiet fill on a light ground (chips, filters)
    | 'lifted' // white on a light ground, raised (product detail)
    | 'glass' // translucent pane over a light ground (product detail CTAs)
    | 'primary-inverse' // white fill on a dark ground
    | 'secondary-dark' // hairline on a dark ground
    | 'ghost'; // label only, no fill and no hairline

/**
 * `border-transparent` sits on the base rather than per kind: a filled button
 * written without one is 2px narrower than the hairline button beside it for
 * the same label, which reads as two different buttons.
 */
const BASE = cn(
    'inline-flex cursor-pointer items-center justify-center gap-[0.5rem] whitespace-nowrap',
    'border border-transparent font-sans font-medium transition-colors',
    'disabled:pointer-events-none disabled:opacity-50',
);

const SIZES: Record<ButtonSize, string> = {
    xs: 'h-[1.5rem] rounded-[0.5rem] px-[0.75rem] text-[0.75rem]',
    s: 'h-[2.25rem] rounded-[1rem] px-[1rem] text-[0.875rem]',
    m: 'h-[2.75rem] rounded-[1rem] px-[1.3125rem] text-[1rem]',
    l: 'h-[3.25rem] rounded-[1.375rem] px-[1.625rem] text-[1rem]',
    xl: 'h-[3.75rem] rounded-[1.375rem] px-[1.75rem] text-[1.125rem]',
};

/** The square (icon-only) button for each step — same height, no label. */
const ICON_SIZES: Record<ButtonSize, string> = {
    xs: 'size-[1.5rem] rounded-[0.5rem] px-0',
    s: 'size-[2.25rem] rounded-[1rem] px-0',
    m: 'size-[2.75rem] rounded-[1rem] px-0',
    l: 'size-[3.25rem] rounded-[1.375rem] px-0',
    xl: 'size-[3.75rem] rounded-[1.375rem] px-0',
};

/*
 * The same two ramps at `md`, for the desktop half of a `ButtonStep` pair.
 *
 * Written out rather than derived from the maps above: Tailwind reads class
 * names out of the source text, so a prefix composed at runtime compiles to no
 * CSS at all — the same silent failure a colour outside the palette has.
 */
const MD_SIZES: Record<ButtonSize, string> = {
    xs: 'md:h-[1.5rem] md:rounded-[0.5rem] md:px-[0.75rem] md:text-[0.75rem]',
    s: 'md:h-[2.25rem] md:rounded-[1rem] md:px-[1rem] md:text-[0.875rem]',
    m: 'md:h-[2.75rem] md:rounded-[1rem] md:px-[1.3125rem] md:text-[1rem]',
    l: 'md:h-[3.25rem] md:rounded-[1.375rem] md:px-[1.625rem] md:text-[1rem]',
    xl: 'md:h-[3.75rem] md:rounded-[1.375rem] md:px-[1.75rem] md:text-[1.125rem]',
};

const MD_ICON_SIZES: Record<ButtonSize, string> = {
    xs: 'md:size-[1.5rem] md:rounded-[0.5rem] md:px-0',
    s: 'md:size-[2.25rem] md:rounded-[1rem] md:px-0',
    m: 'md:size-[2.75rem] md:rounded-[1rem] md:px-0',
    l: 'md:size-[3.25rem] md:rounded-[1.375rem] md:px-0',
    xl: 'md:size-[3.75rem] md:rounded-[1.375rem] md:px-0',
};

/** The step's classes — one size, or the phone size and its `md` growth. */
const stepClasses = (step: ButtonStep, icon: ButtonOptions['icon']): string => {
    const squareOnPhone = icon === true || icon === 'max-md';
    const phoneRamp = squareOnPhone ? ICON_SIZES : SIZES;
    const desktopRamp = icon === true ? MD_ICON_SIZES : MD_SIZES;
    // A square phone step pins the width as well as the height, and the
    // labelled desktop step only names a height — so without this the label
    // has nowhere to grow into and sits clipped inside a 44px box.
    const releaseWidth = icon === 'max-md' ? 'md:w-auto' : '';
    if (typeof step === 'string') {
        // One step named, but the two ends differ: spell the `md` half out.
        return icon === 'max-md'
            ? cn(ICON_SIZES[step], releaseWidth, MD_SIZES[step])
            : phoneRamp[step];
    }
    const [phone, desktop] = step;
    return cn(phoneRamp[phone], releaseWidth, desktopRamp[desktop]);
};

/**
 * The wide soft shadow the lifted family carries. Written as the property
 * rather than `shadow-*`: that utility funnels a single layer through
 * `--tw-shadow`, so a two-layer stack silently resolves to no shadow.
 */
export const LIFT = '[box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)]';

/**
 * What a white surface does when it is pointed at: it darkens a little.
 *
 * The lifted family used to thin its own fill instead (`bg-white/70`), which
 * only reads as a hover where there is something behind it to show through. On
 * a light page that is nothing at all; over a photograph it is the picture
 * coming up through the button, which lightens the label and reads as the
 * control fading rather than responding. Sitting next to a control that answers
 * a hover by opening, the difference was plain.
 *
 * Opaque, so it behaves the same on a page and on a photograph, and shallow
 * enough that a pointer crossing a row of these does not strobe.
 *
 * The whole utility rather than the colour: Tailwind reads class names out of
 * the source text, so a hex interpolated into `hover:bg-[...]` at a call site
 * generates no rule at all.
 */
export const SURFACE_HOVER = 'hover:bg-[#ececec]';

/**
 * The glass surface, read off the reference recording rather than guessed.
 *
 * Sampling a vertical line through one of those buttons, against a page at
 * #fdfdfd: the interior sits at 252-253 — the page's own tone, so the fill is
 * almost entirely transparent — while the top edge dips to 233 and the bottom
 * edge to 202. It is not a white pane. It is a lens that shows only at its
 * rim, lit from above, and the asymmetry between those two edges is the whole
 * effect: a white pane with matching highlights reads flat.
 *
 * Written as the property for the same reason `LIFT` is — this is five layers,
 * and `shadow-*` funnels one through `--tw-shadow` and silently drops the rest.
 */
const GLASS_SHADOW =
    '[box-shadow:inset_0_1.5px_0_0_rgba(255,255,255,0.9),inset_0_1px_3px_0_rgba(255,255,255,0.6),inset_0_-3px_6px_-2px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.05),0_6px_16px_-6px_rgba(0,0,0,0.18)]';

/**
 * Pressed: the lens compresses against the page. The lift underneath
 * collapses and the shadow drawn up from the bottom edge deepens, so the
 * button reads as pushed into the surface rather than merely tinted.
 *
 * Designed rather than sampled — the reference recording has no press in it
 * (frames pulled at 1.5fps from 120fps would likely miss one), so this follows
 * from what the surface is instead of copying a measurement.
 */
const GLASS_SHADOW_PRESSED =
    'active:[box-shadow:inset_0_1px_0_0_rgba(255,255,255,0.7),inset_0_-5px_10px_-3px_rgba(0,0,0,0.22),inset_0_0_0_1px_rgba(0,0,0,0.07),0_2px_5px_-3px_rgba(0,0,0,0.16)]';

/**
 * Mirrors the `Button` variable collection in the Figma file. The two are
 * separate systems kept in step by hand — a change to a Figma variable does
 * not reach the site until it is copied here.
 *
 *   colour/ink              #323232
 *   colour/label-on-ink     #f2f2f2
 *   colour/label-on-light   #323232
 *   colour/hairline         #323232
 *   colour/surface          #ffffff
 *   colour/surface-muted    #ffffff
 */
/**
 * Selected, on a dark ground.
 *
 * White, the same surface the selected control takes on every light page. It
 * was a mid grey, on the reasoning that a white fill against #141414 pulls
 * harder than the photography the page exists to show — but at #4d4d4d
 * against an unselected #141414 the selection was easy to miss, and it made
 * the dark browse pages the one place a selected control did not look like
 * the selected control everywhere else.
 */
export const DARK_SELECTED = 'bg-white text-[#181818]';
export const DARK_SELECTED_HOVER = 'hover:bg-white/90';
export const DARK_SELECTED_ACTIVE = 'active:bg-white/80';

const KINDS: Record<ButtonKind, string> = {
    primary: 'bg-[#323232] text-[#f2f2f2] hover:bg-[#4a4a4a]',
    secondary:
        'border-[#323232] text-[#323232] hover:bg-[#323232] hover:text-[#f2f2f2]',
    // surface-muted is white now, so this differs from `lifted` only by the
    // shadow: a quiet filled control that does not lift off the page.
    muted: 'bg-white text-[#323232] hover:bg-[#f2f2f2]',
    lifted: cn('bg-white text-[#323232]', SURFACE_HOVER, LIFT),
    // Surface only: the size still supplies the corner, the height and the
    // type, so a glass button is the same button in a different material.
    glass: cn(
        // Barely-there fill: the recording's interior is the page's own tone,
        // so the rim does the work. The blur shows nothing over a flat ground
        // and everything if one of these ever sits over a photograph.
        'border-transparent bg-white/35 text-[#323232] backdrop-blur-[6px] backdrop-saturate-150',
        // Hover clears the lens, as it does in the reference.
        'transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-white/60',
        GLASS_SHADOW,
        // A press sinks it a touch. Small on purpose: at 52px a deeper scale
        // reads as the button jumping rather than being pushed.
        'active:scale-[0.985] motion-reduce:active:scale-100',
        GLASS_SHADOW_PRESSED,
    ),
    'primary-inverse': 'bg-white text-[#323232] hover:bg-white/85',
    'secondary-dark':
        'border-white/60 text-white hover:bg-white hover:text-[#323232]',
    ghost: 'text-[#323232] hover:text-[#323232]/70',
};

interface ButtonOptions {
    /**
     * Square icon-only button at the same height.
     *
     * `'max-md'` is square on a phone and labelled from `md` up — the shape a
     * control resting on a tile has to take. A 176px portrait has no room for
     * a label the same button wears beside a 900px frame, and a step small
     * enough to fit one is below the touch target. The call site hides its own
     * label with `max-md:hidden` and keeps the button's accessible name.
     */
    icon?: boolean | 'max-md';
}

/**
 * Compose a platform button's classes. Pass anything layout-related (width,
 * margins, alignment) through `extra` — never a height, corner or padding,
 * which belong to the size.
 */
export const button = (
    kind: ButtonKind,
    size: ButtonStep,
    extra?: string,
    options?: ButtonOptions,
) => cn(BASE, stepClasses(size, options?.icon), KINDS[kind], extra);

/** The raw step values, for the rare caller that has to match a height. */
export const BUTTON_HEIGHT: Record<ButtonSize, string> = {
    xs: '1.5rem',
    s: '2.25rem',
    m: '2.75rem',
    l: '3.25rem',
    xl: '3.75rem',
};
